import React from 'react';
import PropTypes from 'prop-types';

import { TextField } from '../../TextField/component';
import { PreviewDropZone } from '../../Uploader/PreviewDropZone';

import {
  DETAILED_IMAGE_AUTHORING_INPUT_TYPE_UPLOAD,
  DETAILED_IMAGE_AUTHORING_INPUT_TYPE_URL,
  DETAILED_IMAGE_AUTHORING_INPUT_TYPE_EDIT,
} from './consts';

import './style.scss';

export class ImageSourceSettings extends React.PureComponent {
  static propTypes = {
    src: PropTypes.string, // either src or file is required
    // eslint-disable-next-line react/forbid-prop-types
    file: PropTypes.object,
    altText: PropTypes.string,

    srcError: PropTypes.string,
    fileError: PropTypes.string,

    hideRemoveButton: PropTypes.bool,
    srcOnChange: PropTypes.func.isRequired,
    onBrowseClick: PropTypes.func, // if this is enabled, then fileOnChange won't get triggered
    fileOnChange: PropTypes.func,
  };

  static defaultProps = {
    src: '',
    file: null,
    altText: '',

    srcError: null,
    fileError: null,
    hideRemoveButton: false,
    onBrowseClick: undefined,
    fileOnChange: () => {},
  };

  constructor(props) {
    super(props);

    const { src } = props;

    this.fileInputRef = React.createRef();

    this.state = {
      upload: null,
      imageInputType: src
        ? DETAILED_IMAGE_AUTHORING_INPUT_TYPE_EDIT
        : DETAILED_IMAGE_AUTHORING_INPUT_TYPE_UPLOAD,
      focusPreviewDropZone: false,
      focusUrlInput: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { file } = this.props;

    // refresh the image preview if the file has changed
    if (file !== prevProps.file && file) {
      const reader = new FileReader();

      reader.onload = () => {
        this.setState({
          upload: { preview: reader.result },
        });
      };

      reader.readAsDataURL(file);
    }
  }

  // Clear all image related fields and image preview
  resetImage = () => {
    const { fileOnChange, srcOnChange } = this.props;

    fileOnChange(null);
    srcOnChange('');

    this.setState({
      upload: null,
    });
  };

  switchToUpload = () => {
    this.resetImage();
    this.setState({
      imageInputType: DETAILED_IMAGE_AUTHORING_INPUT_TYPE_UPLOAD,
      focusPreviewDropZone: true,
    });
  };

  switchToUrl = () => {
    this.resetImage();
    this.setState({
      imageInputType: DETAILED_IMAGE_AUTHORING_INPUT_TYPE_URL,
      focusUrlInput: true,
    });
  };

  handleUploadClick = () => {
    const { onBrowseClick } = this.props;

    if (onBrowseClick) {
      // only used for RichTextEditor ol_image plugin
      onBrowseClick();
    } else {
      this.fileInputRef.current.click();
    }
  };

  handleDragOver = (event) => {
    event.stopPropagation();
    event.preventDefault();

    // disabled for now
    // const data = event.dataTransfer;
    // data.dropEffect = 'copy';
  };

  handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // disabled for now
    // pass the files to the file input, this will trigger handleFileChanged()
    // this.fileInputRef.current.files = event.dataTransfer.files;
  };

  handleCancelClick = () => {
    this.resetImage();
  };

  handlefileRemoveClick = () => {
    this.switchToUpload();
  };

  handleFileChanged = (e) => {
    const { fileOnChange } = this.props;
    const file = e.target.files[0];

    this.resetImage();

    fileOnChange(file);
  };

  handleSwitchUrlClick = () => {
    this.switchToUrl();
  };

  handleSwitchUploadClick = () => {
    this.switchToUpload();
  };

  render() {
    const {
      src,
      altText,

      srcError,
      fileError,

      hideRemoveButton,
      srcOnChange,
    } = this.props;

    const {
      upload,
      imageInputType,
      focusPreviewDropZone,
      focusUrlInput,
    } = this.state;

    // disabled for now
    // onDragOver={this.handleDragOver}
    // onDrop={this.handleDrop}

    return (
      <image-source-settings>
        <div className="image-source-settings-image">
          {imageInputType === DETAILED_IMAGE_AUTHORING_INPUT_TYPE_UPLOAD && (
            <div className="image-source-settings-image-file">
              <span className="image-source-settings-image-label">
                Upload image file
                {fileError && (
                  <span className="image-source-settings-image-file-error">
                    {' '}
                    {fileError}
                  </span>
                )}
              </span>
              <PreviewDropZone
                upload={upload}
                type="image"
                onUploadClick={this.handleUploadClick}
                onClear={this.handleCancelClick}
                onFileLoad={() => {}}
                displayFill
                canDragDrop={false}
                file={src}
                focusOnMount={focusPreviewDropZone}
                actionIcon="folder-open-o"
                actionLabel="Select a file"
                actionDescription=""
              />
              <input
                className="image-source-settings-image-file-input"
                ref={this.fileInputRef}
                type="file"
                onChange={this.handleFileChanged}
              />
              <p className="image-source-settings-switch">
                Alternatively,{' '}
                <button
                  type="button"
                  className="image-source-settings-switch-button"
                  onClick={this.handleSwitchUrlClick}
                >
                  enter a URL
                </button>
                .
              </p>
            </div>
          )}
          {imageInputType === DETAILED_IMAGE_AUTHORING_INPUT_TYPE_URL && (
            <div className="image-source-settings-src-file">
              <TextField
                className="image-source-settings-field image-source-settings-field-image-url"
                label="Image URL"
                type="text"
                value={src}
                onValueChange={srcOnChange}
                error={srcError}
                focusOnMount={focusUrlInput}
              />
              <p className="image-source-settings-switch">
                Alternatively,{' '}
                <button
                  type="button"
                  className="image-source-settings-switch-button"
                  onClick={this.handleSwitchUploadClick}
                >
                  upload an image
                </button>
                .
              </p>
            </div>
          )}
          {imageInputType === DETAILED_IMAGE_AUTHORING_INPUT_TYPE_EDIT && (
            <div className="image-source-settings-src-preview">
              <div className="image-source-settings-src-preview-image">
                <img
                  className="image-source-settings-src-preview-image-img"
                  alt={altText}
                  src={src}
                />
              </div>
              {!hideRemoveButton && (
                <p className="image-source-settings-src-preview-edit">
                  <button
                    type="button"
                    className="image-source-settings-src-preview-edit-button"
                    onClick={this.handlefileRemoveClick}
                  >
                    Remove image
                  </button>
                </p>
              )}
            </div>
          )}
        </div>
      </image-source-settings>
    );
  }
}
